<template>
  <div class=" py-3 mb-3 bg-gray-950">
    <div class="mx-auto max-w-screen-2xl">
      <Filters
        v-model="filters"
        :filters="availableFilters"
        group="wallet"
      />
    </div>
  </div>

  <div>
    <div
      v-if="!isLoading && tokens.length === 0"
      class="flex flex-1 justify-center items-center text-2xl font-light text-gray-500"
    >
      <div class="py-16">
        No tokens
      </div>
    </div>

    <TokenGrid v-if="isLoading || (!isLoading && tokens.length > 0)">
      <GridListItem
        v-for="(item) in tokens"
        :key="item.id"
        :item="item"
        :collection="item.collection"
        show-collection
        hide-wallet
        @showDetails="() => showTokenDetails(item.id)"
      >
        <template #collection-context>
          <OverlayFilterContext
            @exclude="filters.push({key:'collectionId', type: 'collection', value: { comparator: 'nin', value: [item.collection.id] }})"
            @only="filters.push({key:'collectionId', type: 'collection', value: { comparator: 'in', value: [item.collection.id] }})"
          />
        </template>
      </GridListItem>

      <div
        v-for="i in loadingItems"
        :key="i"
      >
        <GridListItem
          :is-loading="true"
          show-collection
          hide-wallet
        />
      </div>
    </TokenGrid>
  </div>

  <IntersectionObserver
    v-if="!isLoading && !isLoadingMore && hasMore"
    @isShow="loadMore"
  />
</template>

<script setup>
import { COLLECTION_LIST_ITEM_FIELDS } from '@/graphql/collection/fragments/collectionListItemFields.fragment';
import { TOKEN_FIELDS } from '@/graphql/token/fragments/tokenFields.fragment';
import gql from 'graphql-tag';
import {
  computed, reactive, ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { usePremium } from '@/composition/premium';
import usePaginatedQuery from '@/composition/pagination/usePaginatedQuery';
import { useFiltersQuerySync } from '@/composition/filters/useFiltersQuerySync';
import useTokenModal from '@/composition/tokens/useTokenModal';
import { FILTERS, FILTER_TYPES } from '@/constants/filters';
import GridListItem from '../collections/GridListItem.vue';
import IntersectionObserver from '../IntersectionObserver.vue';
import Filters from '../filters/Filters.vue';
import OverlayFilterContext from '../OverlayFilterContext.vue';
import TokenGrid from '../collections/TokenGrid.vue';

const query = gql`
  query GetWalletPortfolioTokens($walletId: String!, $take: Int, $skip: Int, $filters: [FilterArg!]) {
    wallet(where: {id: $walletId}) {
      id
      tokens: tokensV2(take: $take, skip: $skip, filters: $filters) {
        ...Token_Fields
        price
        collection {
          ...CollectionListItem_Fields
        }
      }
    }
  }
  ${TOKEN_FIELDS}
  ${COLLECTION_LIST_ITEM_FIELDS}
`;

const route = useRoute();
const { address } = route.params;

const paginationData = reactive({
  skip: 0,
  take: 28,
});
const availableFilters = FILTERS.WALLET_TOKENS;
const filters = ref([{ key: 'hideSpam', type: FILTER_TYPES.BOOLEAN, value: true }]);
useFiltersQuerySync(filters);

const variables = computed(() => ({
  walletId: address,
  filters: filters.value,
}));

const {
  result,
  loading: isLoading,
  loadMore: fetchMore,
  hasMore,
  loadingMore: isLoadingMore,
} = usePaginatedQuery({
  take: paginationData.take,
  hasMoreCheck: (data) => data.wallet.tokens.length === paginationData.take,
  mergeResults: (oldData, newData) => {
    const data = JSON.parse(JSON.stringify(oldData));
    data.wallet.tokens = [
      ...data.wallet.tokens,
      ...newData.wallet.tokens,
    ];

    return data;
  },
}, query, variables);

const tokens = computed(() => result.value?.wallet?.tokens || []);

const loadingItems = computed(() => (isLoading.value ? 28 : 0));

const { requirePremium } = usePremium();
async function loadMore() {
  await requirePremium('to view all tokens');

  return fetchMore();
}

const { open } = useTokenModal();
function showTokenDetails(tokenId, withBuyIntent = false) {
  open(tokenId);
}

</script>
